import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2, Zap } from 'lucide-react';
import { useMyDialerSessions, formatDuration } from '@/hooks/useDialerAnalytics';

export default function DialerSessions() {
  const navigate = useNavigate();
  const { data: sessions = [], isLoading } = useMyDialerSessions(30);

  const totals = sessions.reduce(
    (acc, s) => {
      acc.dialed += s.total_dialed ?? 0;
      acc.human += s.total_human ?? 0;
      acc.machine += s.total_machine ?? 0;
      return acc;
    },
    { dialed: 0, human: 0, machine: 0 }
  );

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/outbound')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="h-4 w-4 text-orange-500" /> Minhas sessões de discagem
          </h1>
          <p className="text-sm text-muted-foreground">Últimos 30 dias</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Total discado</div>
            <div className="text-2xl font-semibold mt-1">{totals.dialed}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Humanos</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-600">{totals.human}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Taxa humana</div>
            <div className="text-2xl font-semibold mt-1">
              {totals.dialed > 0 ? ((totals.human / totals.dialed) * 100).toFixed(0) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sessões ({sessions.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading && (
            <div className="flex items-center text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Carregando...
            </div>
          )}
          {!isLoading && sessions.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma sessão registrada ainda.</p>
          )}
          {sessions.map((s) => {
            const duration =
              s.started_at && s.ended_at
                ? Math.round((new Date(s.ended_at).getTime() - new Date(s.started_at).getTime()) / 1000)
                : null;
            return (
              <Link
                key={s.id}
                to={`/outbound/discador/sessao/${s.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 hover:bg-muted/50 transition-colors flex-wrap"
              >
                <div>
                  <div className="text-sm font-medium">
                    {new Date(s.started_at ?? s.created_at).toLocaleString('pt-BR')}
                    {s.batch_label ? ` · ${s.batch_label}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {s.total_dialed} discadas · {s.total_human} humanos · {s.total_machine} caixa postal ·{' '}
                    {formatDuration(duration)}
                  </div>
                </div>
                <Badge variant="outline" className="rounded-full text-xs">
                  {s.status === 'active'
                    ? 'Em andamento'
                    : s.status === 'paused'
                    ? 'Pausada'
                    : 'Encerrada'}
                </Badge>
              </Link>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
